import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Images, Languages, LogOut, MoreVertical, Pizza as PizzaIcon, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocalization } from "@/context/localization-context";
import { localeLabels, supportedLocales } from "@/i18n/messages";
import { errorMessage, fetchProducts, readIdentity, setSignedOutHandler, storeIdentity, type StationIdentity, type StationProduct } from "./api";
import { CaptureScreen } from "./capture-screen";
import { LoginScreen } from "./login-screen";
import { RecentPhotos } from "./photos";
import { PizzaPicker } from "./pizza-picker";

/** keeps the tablet's screen on while the app is open (where the browser allows it) */
function useWakeLock() {
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    const request = async () => {
      try {
        lock = (await navigator.wakeLock?.request("screen")) ?? null;
      } catch {
        // not allowed (battery saver, no https): the screen times out as usual
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void request();
    };
    void request();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      void lock?.release().catch(() => undefined);
    };
  }, []);
}

/** the station's web app manifest (installable, full screen), only on the station's pages */
function useStationManifest() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/station.webmanifest";
    document.head.appendChild(link);
    document.title = "Pizza Checker";
    return () => link.remove();
  }, []);
}

/**
 * The station tablet app (/station): the staff picks the pizza that lies under the camera and has
 * it photographed, and sees the photo; the backend evaluates it against the pizza's recipe.
 * Results are not shown to the staff yet (data collection).
 */
export function StationApp() {
  const [identity, setIdentity] = useState<StationIdentity | null>(readIdentity);
  useWakeLock();
  useStationManifest();

  useEffect(() => {
    setSignedOutHandler(() => setIdentity(null));
    return () => setSignedOutHandler(null);
  }, []);

  const signOut = useCallback(() => {
    storeIdentity(null);
    setIdentity(null);
  }, []);

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-background text-foreground [overscroll-behavior:none]">
      {identity ? <Station identity={identity} onSignOut={signOut} /> : <LoginScreen onSignedIn={setIdentity} />}
    </div>
  );
}

function Station({ identity, onSignOut }: { identity: StationIdentity; onSignOut: () => void }) {
  const { t } = useLocalization();
  const [selected, setSelected] = useState<StationProduct | null>(null);
  const [photosOpen, setPhotosOpen] = useState(false);
  const back = useCallback(() => setSelected(null), []);
  const products = useQuery({
    queryKey: ["station", "products", identity.login],
    queryFn: fetchProducts,
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
    retry: 2,
  });

  return (
    <>
      <AppBar
        title={products.data?.branch?.name ?? t("station.title")}
        subtitle={identity.login}
        onReload={() => void products.refetch()}
        reloading={products.isFetching}
        onPhotos={() => setPhotosOpen(true)}
        onSignOut={onSignOut}
      />
      <main className="min-h-0 flex-1">
        {selected ? (
          <CaptureScreen product={selected} onDone={back} />
        ) : (
          <PizzaPicker
            products={products.data?.products}
            loading={products.isLoading}
            error={products.error ? errorMessage(products.error) : null}
            onRetry={() => void products.refetch()}
            onPick={setSelected}
          />
        )}
      </main>
      {photosOpen && <RecentPhotos onClose={() => setPhotosOpen(false)} />}
    </>
  );
}

function AppBar({
  title,
  subtitle,
  onReload,
  reloading,
  onPhotos,
  onSignOut,
}: {
  title: string;
  subtitle: string;
  onReload: () => void;
  reloading: boolean;
  onPhotos: () => void;
  onSignOut: () => void;
}) {
  const { t, locale, setLocale } = useLocalization();
  return (
    <header className="flex h-16 shrink-0 items-center gap-3 border-b bg-card px-4 shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
      <PizzaIcon className="h-7 w-7 shrink-0 text-amber-400" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="truncate text-lg font-semibold leading-tight">{title}</div>
        <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
      </div>
      <Button variant="ghost" className="h-12 gap-2 rounded-full px-4 text-base" onClick={onPhotos}>
        <Images className="h-6 w-6" />
        <span className="hidden sm:inline">{t("station.recent.title")}</span>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-12 w-12 rounded-full p-0" aria-label={t("station.menu")}>
            <MoreVertical className="h-6 w-6" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[14rem]">
          <DropdownMenuItem className="h-11 gap-3 text-base" onSelect={onReload} disabled={reloading}>
            <RefreshCw className="h-5 w-5" />
            {t("station.reload")}
          </DropdownMenuItem>
          {supportedLocales
            .filter((option) => option !== locale)
            .map((option) => (
              <DropdownMenuItem key={option} className="h-11 gap-3 text-base" onSelect={() => setLocale(option)}>
                <Languages className="h-5 w-5" />
                {localeLabels[option]}
              </DropdownMenuItem>
            ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="h-11 gap-3 text-base" onSelect={onSignOut}>
            <LogOut className="h-5 w-5" />
            {t("station.signOut")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
